import { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X } from 'lucide-react';
import { Avatar, AvatarImage, Button, Label } from '@bliks-io/ui';
import type { SystemLogoData } from '@bliks-io/contracts';

interface SystemLogoUploadProps {
  /**
   * Current logo data from the system
   */
  logo?: SystemLogoData;

  /**
   * File being uploaded (preview state)
   */
  file: File | null;

  /**
   * System name for fallback avatar
   */
  systemName: string;

  /**
   * Whether the upload is in progress
   */
  loading?: boolean;

  /**
   * Callback when file is selected
   */
  onFileChange: (file: File | null) => void;

  /**
   * Callback when logo should be removed
   */
  onRemove: () => void;
}

/**
 * SystemLogoUpload Component
 *
 * A reusable component for uploading and managing system logos.
 * Supports both SVG and raster images, with automatic thumbnail display.
 */
export function SystemLogoUpload({
  logo,
  file,
  systemName,
  loading = false,
  onFileChange,
  onRemove
}: SystemLogoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const avatarSrc = useMemo(() => {
    // Priority 1: Show preview of file being uploaded
    if (file) {
      return URL.createObjectURL(file);
    }

    // Priority 2: Show existing logo
    if (logo) {
      if (logo.type === 'svg') {
        return logo.svg;
      } else {
        // For raster, use thumbnail (WebP preferred, PNG fallback)
        return logo.thumbnail?.webp || logo.thumbnail?.png;
      }
    }

    return undefined;
  }, [file, logo]);

  const handleEdit = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    onFileChange(selectedFile);
    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  const hasLogo = Boolean(file || logo);

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">{t('common.Logo')}</Label>

      <div className="flex items-center gap-4">
        {avatarSrc && (
          <Avatar size="lg">
            <AvatarImage src={avatarSrc} alt={systemName} />
          </Avatar>
        )}

        <div className="flex flex-col gap-2 flex-1">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="small"
              variant="secondary"
              onClick={handleEdit}
              loading={loading}
              disabled={loading}
            >
              <Upload className="w-4 h-4 mr-2" />
              {hasLogo ? t('common.Change') : t('common.Upload')}
            </Button>

            {hasLogo && (
              <Button
                type="button"
                size="small"
                variant="outline"
                onClick={onRemove}
                disabled={loading}
              >
                <X className="w-4 h-4 mr-2" />
                {t('common.Remove')}
              </Button>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            {t('OrgSettingPage.logoUploadHint', 'SVG, PNG, JPG or WebP (max 10MB)')}
          </p>
        </div>
      </div>

      <input
        ref={inputRef}
        onChange={handleFileChange}
        type="file"
        className="hidden"
        accept="image/png, image/jpeg, image/svg+xml, image/webp"
      />
    </div>
  );
}
